import { useEffect, useLayoutEffect, useRef, useCallback } from "react"
import { gql, useApolloClient, useQuery } from "@apollo/client"
import { uniqBy } from "lodash"

import { useFiltersContext } from "../contexts/FiltersProvider"
import { useProjectsContext } from "../contexts/ProjectsProvider"
import { useAppContext } from "../contexts/AppProvider"
import { useNavigate } from "react-router-dom"
import { useToast } from "@chakra-ui/react"
import { toastFailed } from "../bootstrap/config"

const PROJECTS_W_FORMS = gql`
  query ($searchInput: SearchInput!, $pagination: Pagination) {
    searchProjects(searchInput: $searchInput, pagination: $pagination) {
      projects {
        _id
        clientId
        reference
        status
        clientName
        projectLeadId
        projectLead {
          _id
          imgUrl
          displayName
        }
        testers {
          _id
          imgUrl
          displayName
        }
        trainees {
          _id
          imgUrl
          displayName
        }
        groupId
        forms {
          _id
          name
          index
          icon
          allowMultipleIterations
          pages {
            _id
            index
            name
            sections {
              _id
              index
              name
              description
              rows {
                _id
                index
                fields {
                  _id
                  name
                  type
                  label
                  required
                  validations
                  variant
                  placeholder
                  tooltip
                  canSelectFutureDate
                  clearAfterEdit
                  saveDateOfSignature
                  useRichTextLabelInput
                  secondarylabel
                  requiredWhen
                  data
                  options {
                    value
                    label
                    metadata {
                      isBold
                    }
                  }
                  image
                  rows {
                    value
                    label
                  }
                  columns {
                    type
                    value
                    label
                    placeholder
                    options {
                      value
                      label
                      metadata {
                        isBold
                      }
                    }
                    variant
                    required
                    validations
                    hideInTable
                    visibleWhen
                    otherSpecificationField
                  }
                }
              }
            }
          }
          iterations {
            status
            values
          }
        }
      }
      total
    }
  }
`
const PROJECTS = gql`
  query ($searchInput: SearchInput!, $pagination: Pagination) {
    searchProjects(searchInput: $searchInput, pagination: $pagination) {
      projects {
        _id
        clientId
        reference
        status
        clientName
        projectLeadId
        projectLead {
          _id
          imgUrl
          displayName
        }
        testers {
          _id
          imgUrl
          displayName
        }
        trainees {
          _id
          imgUrl
          displayName
        }
        projectTemplate {
          name
          category
        }
        groupId
      }
      total
    }
  }
`

const useGetProjects = ({
  sortBy,
  sortDirection,
  projectId,
  withForms,
}: {
  sortBy?: string
  sortDirection?: string
  projectId?: string
  withForms?: boolean
}) => {
  const navigate = useNavigate()
  const toast = useToast()
  const client = useApolloClient()
  const { settings } = useAppContext()
  const { setProjects, setCurrentProject, setTotalProjects } =
    useProjectsContext()

  /** Route / hook `projectId` changed — drop previous project immediately so UI cannot show wrong project until Apollo returns. */
  useLayoutEffect(() => {
    if (!projectId) return
    setCurrentProject((prev) => {
      if (!prev || prev._id === projectId) {
        return prev
      }
      return null
    })
  }, [projectId, withForms, setCurrentProject])
  const { filters } = useFiltersContext()

  const queryVariables = {
    searchInput: projectId ? { _id: projectId } : filters,
    pagination: {
      offset: 0,
      limit: settings?.projectsPerPage,
      sortBy,
      sortDirection,
    },
  }

  const getSingleProjectQueryVars = useCallback(
    (projectIdToUpdate: string) => ({
      searchInput: { _id: projectIdToUpdate },
      pagination: {
        offset: 0,
        limit: settings?.projectsPerPage,
        sortBy,
        sortDirection,
      },
    }),
    [settings?.projectsPerPage, sortBy, sortDirection]
  )

  // Track in-flight requests to prevent race conditions
  const inFlightRequestsRef = useRef(new Set<number>())
  const currentFiltersRef = useRef(filters)

  const { loading, data, error, refetch, fetchMore } = useQuery(
    withForms ? PROJECTS_W_FORMS : PROJECTS,
    {
      variables: queryVariables,
      fetchPolicy: "network-only",
      skip: !settings?.projectsPerPage,
    }
  )

  const updateCachedFormValues = useCallback(
    (projectIdToUpdate: string, formId: string, iterationIndex: number, values: Record<string, unknown>) => {
      if (!withForms || !projectIdToUpdate) return
      try {
        const vars = getSingleProjectQueryVars(projectIdToUpdate)
        const cached = client.readQuery({
          query: PROJECTS_W_FORMS,
          variables: vars,
        }) as { searchProjects?: { projects?: Array<{ _id: string; forms?: Array<{ _id: string; iterations?: Array<{ values?: Record<string, unknown> }> }> }> } } | null
        if (!cached?.searchProjects?.projects?.length) return
        const project = cached.searchProjects.projects.find((p) => p._id === projectIdToUpdate) ?? cached.searchProjects.projects[0]
        const formIdx = project.forms?.findIndex((f) => f._id === formId) ?? -1
        if (formIdx === -1 || !project.forms?.[formIdx]?.iterations?.[iterationIndex]) return
        const existing = project.forms[formIdx].iterations[iterationIndex].values ?? {}
        const merged = { ...existing, ...values }
        const updated = JSON.parse(JSON.stringify(cached))
        const proj = updated.searchProjects.projects.find((p: { _id: string }) => p._id === projectIdToUpdate) ?? updated.searchProjects.projects[0]
        if (proj.forms?.[formIdx]?.iterations?.[iterationIndex]) {
          proj.forms[formIdx].iterations[iterationIndex].values = merged
        }
        client.writeQuery({
          query: PROJECTS_W_FORMS,
          variables: vars,
          data: updated,
        })
      } catch (e) {
        console.warn('[useGetProjects] updateCachedFormValues failed', e)
      }
    },
    [client, withForms, getSingleProjectQueryVars]
  )

  // Update current filters ref when filters change
  useEffect(() => {
    currentFiltersRef.current = filters
    // Clear in-flight requests when filters change
    inFlightRequestsRef.current.clear()
  }, [filters])

  useEffect(() => {
    // filters change, update the query
    if (data) {
      setProjects(data.searchProjects.projects)
      setTotalProjects(data.searchProjects.total)
    }
  }, [filters]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (projectId) {
      if (data) {
        if (data.searchProjects.projects.length > 0) {
          const project = data.searchProjects.projects[0]
          // Apollo can briefly return the previous query result while variables change; do not
          // overwrite context with the wrong project (causes blank / wrong form until refresh).
          if (project._id === projectId) {
            setCurrentProject(project)
            setTotalProjects(data.searchProjects.total)
          } else {
            setCurrentProject(null)
          }
        } else {
          toast({ ...toastFailed, description: "Project not found" })
          navigate("/")
        }
      }
    } else {
      // data changes, update projects
      if (data) {
        setProjects(data.searchProjects.projects)
        setTotalProjects(data.searchProjects.total)
      }
    }
  }, [data, projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreProjects = useCallback(
    async (page: number) => {
      if (!settings?.projectsPerPage) return

      // Check if this request is already in flight
      if (inFlightRequestsRef.current.has(page)) {
        return
      }

      // Check if filters have changed since this request was initiated
      const filtersChanged =
        JSON.stringify(currentFiltersRef.current) !== JSON.stringify(filters)
      if (filtersChanged) {
        return
      }

      // Mark this request as in flight
      inFlightRequestsRef.current.add(page)

      try {
        const res = await fetchMore({
          variables: {
            searchInput: currentFiltersRef.current, // Use the filters from when request was initiated
            pagination: {
              offset: page * settings.projectsPerPage,
              limit: settings.projectsPerPage,
              sortBy,
              sortDirection,
            },
          },
        })

        // Double-check filters haven't changed during the request
        const filtersStillValid =
          JSON.stringify(currentFiltersRef.current) === JSON.stringify(filters)
        if (
          filtersStillValid &&
          res.data?.searchProjects?.projects?.length > 0
        ) {
          setProjects((projects) =>
            uniqBy(
              [...(projects || []), ...res.data.searchProjects.projects],
              "_id"
            )
          )
        }
      } catch (error) {
        console.error("Error loading more projects:", error)
      } finally {
        // Remove from in-flight requests
        inFlightRequestsRef.current.delete(page)
      }
    },
    [
      settings?.projectsPerPage,
      sortBy,
      sortDirection,
      filters,
      fetchMore,
      setProjects,
    ]
  )

  // Cleanup in-flight requests on unmount
  useEffect(() => {
    const inFlightRequests = inFlightRequestsRef.current
    return () => {
      inFlightRequests.clear()
    }
  }, [])

  return {
    loading,
    data,
    error,
    refetch,
    loadMoreProjects,
    ...(withForms ? { updateCachedFormValues } : {}),
  }
}

export default useGetProjects
